import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { NameIdObj } from 'common/models/GenericTypes';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import { CandidatesNotRejectedReportFilter } from 'common/models/Reporting/AnalyticsFilterData';
import { CandidatesNotRejectedReportRecord } from 'common/models/Reports/CandidatesNotRejectedReportRecord';
import GridWithStateWrapper from 'components/GridWidthStateWrapper';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import PreviewLoaderComponent from 'components/Previews/PreviewLoader';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GetJobCandidateStatuses, GetJobsSettings } from 'services/ConfigurationService';
import { GetCandidatesNotRejectedReportData } from 'services/JobsService';
import { GetMyUser } from 'services/UsersService';

interface Props {
  description: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const filterElementStyle: SxProps = { flex: '1 1 0' };
const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

export default function CandidatesNotRejectedReport({
  description,
  loadingHandler,
  errorHandler,
}: Props) {
  const [isOnHoldActive, setIsOnHoldActive] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [sourceType, setSourceType] = useState(4);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [status, setStatus] = useState('Open');
  const [timeSinceApplication, setTimeSinceApplication] = useState(14);
  const [workflowSearchDirection, setWorkflowSearchDirection] = useState('before');
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState<NameIdObj | null>(null);
  const [workflowStatuses, setWorkflowStatuses] = useState<NameIdObj[]>([]);
  const [rows, setRows] = useState<CandidatesNotRejectedReportRecord[]>([]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showTagsPrevewNoDelay, setShowTagsPreviewNoDelay] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [isPreviewTags, setIsPreviewTags] = useState(false);
  const [previewRecordName, setPreviewRecordName] = useState('');
  const apiRef = useGridApiRef();

  useEffect(() => {
    const setupData = async () => {
      const [me, settings, statuses] = await Promise.all([
        GetMyUser(errorHandler),
        GetJobsSettings(errorHandler),
        GetJobCandidateStatuses(errorHandler),
      ]);

      if (me) setSelectedUsers([me.userID]);
      if (settings) {
        const onHoldSetting = settings.find((v) => v.type === 'OnHold');
        if (onHoldSetting) setIsOnHoldActive(onHoldSetting.value === 'True');
      }
      if (statuses) {
        const enabledOnly = statuses.filter((s) => s.enabled);
        const shortlisted = enabledOnly.find((s) => s.jobCandidateSystemStatusId === 4);
        if (shortlisted) setSelectedWorkflowStatus({ id: shortlisted.id, name: shortlisted.name });
        setWorkflowStatuses(enabledOnly.map((v) => ({ id: v.id, name: v.name })));
      }
    };
    setupData();
  }, [errorHandler]);

  const runReportCallback = useCallback(async () => {
    let sIds: number[] = [];
    if (sourceType === 3) sIds = selectedGroups;
    if (sourceType === 4) sIds = selectedUsers;

    const filter: CandidatesNotRejectedReportFilter = {
      jobStatus: status,
      sourceType: sourceType,
      sourceIds: sIds,
      timeSinceApplication: timeSinceApplication,
      workflowSearchDirection: workflowSearchDirection,
      workflowStatusId: selectedWorkflowStatus?.id ?? 0,
    };
    loadingHandler?.(true);
    const res = await GetCandidatesNotRejectedReportData(filter, errorHandler);
    if (res) setRows(res);
    setShowGrid(true);
    loadingHandler?.(false);
  }, [
    sourceType,
    selectedGroups,
    selectedUsers,
    status,
    timeSinceApplication,
    workflowSearchDirection,
    selectedWorkflowStatus,
    errorHandler,
    loadingHandler,
  ]);

  const columns = useMemo<GridColDef[]>(() => {
    const handlePreviewHover = (
      type: PreviewEntityType | '',
      id: number,
      isTags = false,
      recordName = '',
      noDelay = false
    ) => {
      setPreviewType(type);
      setPreviewRecordId(id);
      setIsPreviewTags(isTags);
      setPreviewRecordName(recordName);
      setIsPreviewOpen(true);
      setShowTagsPreviewNoDelay(noDelay);
    };

    const handlePreviewClose = () => {
      setIsPreviewOpen(false);
      setShowTagsPreviewNoDelay(false);
    };

    const dateValueGetter: GridValueGetter<
      CandidatesNotRejectedReportRecord,
      any,
      undefined,
      string
    > = (value) => {
      if (value) {
        const d = moment(value);
        if (d.isValid() && d.get('year') > 1) {
          return d.toDate();
        }
      }
      return null;
    };

    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY');
      }
    };

    const linkToJobRenderer = (params: GridRenderCellParams) => {
      const jobId = params.row.jobID;
      if (params.value && jobId) {
        return (
          <Link
            to={`/jobs/${jobId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('job', jobId)}
            onMouseLeave={handlePreviewClose}
            target="_blank"
          >
            {params.value}
          </Link>
        );
      }
      return null;
    };

    const linkToClientRenderer = (params: GridRenderCellParams) => {
      const clientId = params.row.clientID;
      if (params.value && clientId) {
        return (
          <Link
            to={`/clients/${clientId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('client', clientId)}
            onMouseLeave={handlePreviewClose}
            target="_blank"
          >
            {params.value}
          </Link>
        );
      }
      return null;
    };

    const linkToCandidateRenderer = (params: GridRenderCellParams) => {
      const candidateId = params.row.candidateID;
      if (params.value && candidateId) {
        return (
          <Link
            to={`/candidates/${candidateId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('candidate', candidateId)}
            onMouseLeave={handlePreviewClose}
            target="_blank"
          >
            {params.value}
          </Link>
        );
      }
      return null;
    };

    return [
      { field: 'jobID', headerName: 'Job ID', renderCell: linkToJobRenderer, width: 150 },
      { field: 'jobTitle', headerName: 'Job Title', renderCell: linkToJobRenderer, width: 200 },
      { field: 'jobType', headerName: 'Job Type', width: 150 },
      {
        field: 'clientName',
        headerName: 'Client Name',
        renderCell: linkToClientRenderer,
        width: 200,
      },
      {
        field: 'candidateName',
        headerName: 'Candidate Name',
        renderCell: linkToCandidateRenderer,
        width: 200,
      },
      { field: 'ratingName', headerName: 'Rating', width: 150 },
      { field: 'ratingReason', headerName: 'Rating Reason', width: 150 },
      { field: 'comments', headerName: 'Comments', width: 250 },
      { field: 'currentStatusName', headerName: 'Status', width: 150 },
      { field: 'furthestStatusName', headerName: 'Furthest Status', width: 150 },
      {
        field: 'statusUpdatedDate',
        headerName: 'Status Updated',
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
        type: 'date',
        width: 150,
      },
      {
        field: 'applicationDate',
        headerName: 'Application Date',
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
        type: 'date',
        width: 150,
      },
      { field: 'source', headerName: 'Source', width: 150 },
      { field: 'consultantName', headerName: 'Consultant', width: 150 },
    ];
  }, []);

  const onGroupsChange = useCallback((groups: NameIdObj[]) => {
    setSelectedGroups(groups.map((u) => u.id));
  }, []);

  const onUsersChange = useCallback((users: NameIdObj[]) => {
    setSelectedUsers(users.map((u) => u.id));
  }, []);

  const workflowStatusChangeHandler = useCallback((e: any, v: NameIdObj | null) => {
    setSelectedWorkflowStatus(v);
  }, []);

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        isTagsPreview={isPreviewTags}
        showDelayMs={showTagsPrevewNoDelay ? 0 : undefined}
        titleOverride={previewRecordName}
        kioskToken={null}
      />
      <Box display="flex" gap={1} mb={1}>
        <TextField
          select
          label="Source Type"
          value={sourceType.toString()}
          onChange={({ target }) => setSourceType(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="1">All</MenuItem>
          <MenuItem value="3">Group</MenuItem>
          <MenuItem value="4">User</MenuItem>
        </TextField>
        <TextField
          select
          label="Job Status"
          value={status}
          onChange={({ target }) => setStatus(target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Open">Open</MenuItem>
          {isOnHoldActive && <MenuItem value="Hold">Hold</MenuItem>}
          <MenuItem value="Closed">Closed</MenuItem>
        </TextField>
        <TextField
          select
          label="Time Since Application"
          value={timeSinceApplication.toString()}
          onChange={({ target }) => setTimeSinceApplication(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="0">All</MenuItem>
          <MenuItem value="7">7 Days</MenuItem>
          <MenuItem value="14">14 Days</MenuItem>
          <MenuItem value="30">30 Days</MenuItem>
          <MenuItem value="60">60 Days</MenuItem>
          <MenuItem value="90">90 Days</MenuItem>
        </TextField>
        <TextField
          select
          label="Workflow Search Direction"
          value={workflowSearchDirection}
          onChange={({ target }) => setWorkflowSearchDirection(target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="before">Before</MenuItem>
          <MenuItem value="after">After</MenuItem>
        </TextField>
        <Autocomplete
          options={workflowStatuses}
          value={selectedWorkflowStatus}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={filterElementStyle}
          onChange={workflowStatusChangeHandler}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Workflow Status"
              slotProps={{ htmlInput: { ...params.inputProps, 'data-lpignore': 'true' } }}
            />
          )}
        />
        <Button variant="contained" color="success" onClick={runReportCallback}>
          Run Report
        </Button>
      </Box>
      <Box display={sourceType === 3 ? undefined : 'none'} mb={1}>
        <MultipleGroupPicker value={selectedGroups} onSelect={onGroupsChange} />
      </Box>
      <Box display={sourceType === 4 ? undefined : 'none'} mb={1}>
        <MultipleUserPicker includeInactiveUsers value={selectedUsers} onSelect={onUsersChange} />
      </Box>
      <Typography component="div" variant="h6" mt={2}>
        Report Description
      </Typography>
      <Typography component="div" variant="body2">
        {description}
      </Typography>
      {showGrid && (
        <GridWithStateWrapper
          gridName="candidates-not-rejected-report"
          rows={rows}
          columns={columns}
          apiRef={apiRef}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
        />
      )}
    </>
  );
}
